import {
  castErrorResult,
  createErrorResult,
  createSuccessResult,
  ERRORS,
  IFiscalClient,
  IZReport,
  SimpleResult,
} from "@prostocheck/shared";

import { requestZReport } from "../commands";
import { mapWithConcurrency } from "../concurrency";
import {
  IClosedShift,
  IReadPeriodParams,
  readShifts,
  toClosedShifts,
} from "../shifts";
import { parseZReport } from "../z-report-xml";

/**
 * One closed shift's Z-report. Fails with `documentNotFound` when ДПС has
 * nothing for that number — an offline shift whose documents have not arrived
 * yet, for instance.
 */
export const readZReport = async ({
  client,
  registrarFiscalNumber,
  shift,
}: {
  client: IFiscalClient;
  registrarFiscalNumber: string;
  shift: IClosedShift;
}): Promise<SimpleResult<IZReport>> => {
  const result = await requestZReport({
    client,
    registrarFiscalNumber,
    documentFiscalNumber: shift.zReportFiscalNumber,
  });

  if (!result.success) {
    return castErrorResult(result);
  }

  if (!result.data?.Data) {
    return createErrorResult(
      ERRORS.fiscal.documentNotFound,
      shift.zReportFiscalNumber,
    );
  }

  return createSuccessResult(
    parseZReport({
      data: result.data.Data,
      fiscalNumber: shift.zReportFiscalNumber,
      shiftNumber: shift.shiftId,
      openedAt: shift.openedAt,
      closedAt: shift.closedAt,
    }),
  );
};

/**
 * Every Z-report of a register over a period, newest first. Test shifts are
 * excluded: their documents carry fiscal numbers but no fiscal meaning.
 *
 * A report that cannot be fetched is skipped rather than failing the whole
 * period — a partial week is more useful than an error, and the count of
 * shifts tells the user something is missing.
 */
export const readZReports = async ({
  client,
  registrarFiscalNumber,
  from,
  to,
}: IReadPeriodParams): Promise<SimpleResult<IZReport[]>> => {
  const shiftsResult = await readShifts({
    client,
    registrarFiscalNumber,
    from,
    to,
  });

  if (!shiftsResult.success) {
    return castErrorResult(shiftsResult);
  }

  const shifts = toClosedShifts(shiftsResult.data ?? []).filter(
    shift => !shift.isTesting,
  );

  const results = await mapWithConcurrency({
    items: shifts,
    map: shift => readZReport({ client, registrarFiscalNumber, shift }),
  });

  const reports = results
    .filter(result => result.success && result.data)
    .map(result => result.data!)
    .sort((a, b) => b.closedAt.localeCompare(a.closedAt));

  return createSuccessResult(reports);
};
